import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { addToChainAfter, Handler } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    CreateDataForUnparse as DefaultCreateDataForUnparse,
} from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/ExportWishList";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { MGBW_CONFIGURATOR_PRICE, MGBW_IS_CONFIGURABLE, MGBW_SELECTIONS } from "@tSurya/Common/MgbwPropertyKeys";
import { MgbwSelectionEntry } from "@tSurya/Common/MgbwConfiguratorHelpers";

type HandlerType = Handler<
    { wishListId: string },
    {
        apiResult: WishListModel;
        dataForUnparse: { fields: string[]; data: any[][] };
    }
>;

type SelectionColumn = {
    optionId: string;
    header: string;
};

const parseVisibleSelections = (raw?: string): MgbwSelectionEntry[] => {
    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return (parsed as MgbwSelectionEntry[]).filter(selection => !!selection && selection.Visible !== false);
    } catch {
        return [];
    }
};

const getSelectionColumnHeader = (selection: MgbwSelectionEntry) => {
    const optionName = selection.Name || "Option";
    return `${optionName}`;
};

const getSelectionCellValue = (selection?: MgbwSelectionEntry) => {
    if (!selection) {
        return "";
    }

    return `${selection.Value || ""} (ID:${selection.OptionId}:${selection.ValueId || ""})`;
};

export const CreateDataForUnparseExtended: HandlerType = props => {
    const { wishListLineCollection } = props.apiResult;
    if (!wishListLineCollection?.length || !props.dataForUnparse) {
        return;
    }

    const selectionColumns: SelectionColumn[] = [];
    const selectionColumnsByOptionId: Record<string, SelectionColumn> = {};

    for (const wishListLine of wishListLineCollection) {
        const selections = parseVisibleSelections(wishListLine.properties?.[MGBW_SELECTIONS]);

        for (const selection of selections) {
            if (!selection.OptionId || selectionColumnsByOptionId[selection.OptionId]) {
                continue;
            }

            const selectionColumn: SelectionColumn = {
                optionId: selection.OptionId,
                header: getSelectionColumnHeader(selection),
            };
            selectionColumnsByOptionId[selection.OptionId] = selectionColumn;
            selectionColumns.push(selectionColumn);
        }
    }

    props.dataForUnparse.fields.push(...selectionColumns.map(column => column.header));
    const priceColumnIndex = props.dataForUnparse.fields.findIndex(field => field === "Price");

    for (let i = 0; i < wishListLineCollection.length; i++) {
        const wishListLine = wishListLineCollection[i];
        const row = props.dataForUnparse.data[i];
        if (!row) {
            continue;
        }

        const selections = parseVisibleSelections(wishListLine.properties?.[MGBW_SELECTIONS]);
        const selectionValuesByOptionId = selections.reduce<Record<string, MgbwSelectionEntry>>((accumulator, item) => {
            if (item.OptionId) {
                accumulator[item.OptionId] = item;
            }
            return accumulator;
        }, {});

        row.push(...selectionColumns.map(column => getSelectionCellValue(selectionValuesByOptionId[column.optionId])));

        // Set configurator price.
        if (wishListLine.properties?.[MGBW_IS_CONFIGURABLE] == "true" && priceColumnIndex !== -1) {
            row[priceColumnIndex] = wishListLine.properties?.[MGBW_CONFIGURATOR_PRICE] || "";
        }
    }
    
    // Format all prices as currency
    const { session } = props.getState().context;
    const language = session.language;
    const currencySymbol = session.currency?.currencySymbol;
    if (priceColumnIndex !== -1 && language && currencySymbol) {
        for (const row of props.dataForUnparse.data) {
            const amount = parseFloat(row[priceColumnIndex]);
            if (!isNaN(amount)) {
                row[priceColumnIndex] = getLocalizedCurrency({ amount, language, currencySymbol });
            }
        }
    }
};

addToChainAfter(chain, DefaultCreateDataForUnparse, CreateDataForUnparseExtended);
